import { useCallback, useEffect, useLayoutEffect, useState, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router';
import {
  storage,
  AUTH_TOKEN_KEY,
  AUTH_USER_KEY,
} from '@/utils/storage';
import { login as apiLogin, logout as apiLogout } from '@/api/auth';
import { AUTH_EXPIRED_EVENT } from '@/api/client';
import { ApiException } from '@/api/types';
import { clearPatientCache } from '@/api/patients';
import { clearPatientProfileCache, getPatientBasicProfile } from '@/api/activities';
import { clearDoctorProfileCache, getDoctorProfile } from '@/api/doctorProfile';
import type { User, LoginCredentials } from '@/types/auth';
import { getUserRole, validateUserRole } from '@/types/auth';
import { useToast } from './toastContextBase';
import { warn, error as logError } from '@/utils/logger';
import { AuthContext } from './authContextBase';

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (!parts[1]) return null;

    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const json = atob(padded);
    const payload = JSON.parse(json);
    return payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState<ApiException | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();

  const hydrateUserProfile = useCallback(async (
    baseUser: User,
    options?: { forceRefresh?: boolean; trackStatus?: boolean }
  ): Promise<User> => {
    const shouldTrackStatus = options?.trackStatus ?? false;

    if (shouldTrackStatus) {
      setIsProfileLoading(true);
      setProfileError(null);
    }

    if (baseUser.role === 'doctor') {
      try {
        const profile = await getDoctorProfile(options?.forceRefresh);
        const firstName = profile.name?.first_name?.trim();
        const lastName = profile.name?.last_name?.trim();
        const email = profile.email?.trim();

        if (!firstName && !lastName && !email) {
          return baseUser;
        }

        return {
          ...baseUser,
          firstName: firstName || baseUser.firstName,
          lastName: lastName || baseUser.lastName,
          email: email || baseUser.email,
        };
      } catch (error) {
        if (shouldTrackStatus) {
          setProfileError(
            error instanceof ApiException
              ? error
              : new ApiException({
                  message:
                    error instanceof Error ? error.message : 'Failed to load doctor profile',
                })
          );
        }
        return baseUser;
      } finally {
        if (shouldTrackStatus) {
          setIsProfileLoading(false);
        }
      }
    }

    const isSelfServiceUser = baseUser.role === 'patient' || baseUser.role === 'nushu-x';
    if (!isSelfServiceUser || (baseUser.firstName && baseUser.lastName)) {
      if (shouldTrackStatus) {
        setIsProfileLoading(false);
        setProfileError(null);
      }
      return baseUser;
    }

    try {
      const profile = await getPatientBasicProfile();
      const firstName = profile.name?.first_name?.trim();
      const lastName = profile.name?.last_name?.trim();

      if (!firstName && !lastName) return baseUser;

      return {
        ...baseUser,
        firstName: firstName || baseUser.firstName,
        lastName: lastName || baseUser.lastName,
      };
    } catch {
      return baseUser;
    } finally {
      if (shouldTrackStatus) {
        setIsProfileLoading(false);
        setProfileError(null);
      }
    }
  }, []);

  const persistHydratedUser = useCallback((previousUser: User, hydratedUser: User) => {
    const changed =
      hydratedUser.firstName !== previousUser.firstName ||
      hydratedUser.lastName !== previousUser.lastName ||
      hydratedUser.email !== previousUser.email;

    if (changed) {
      storage.set(AUTH_USER_KEY, hydratedUser);
      setUser(hydratedUser);
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    if (!user) return;

    const hydratedUser = await hydrateUserProfile(user, {
      forceRefresh: true,
      trackStatus: user.role === 'doctor',
    });
    persistHydratedUser(user, hydratedUser);
  }, [hydrateUserProfile, persistHydratedUser, user]);

  useEffect(() => {
    const storedUser = storage.get<User>(AUTH_USER_KEY);
    const storedToken = storage.get<string>(AUTH_TOKEN_KEY);

    if (storedUser && storedToken) {
      setUser(storedUser);

      const isSelfServiceUser =
        storedUser.role === 'patient' || storedUser.role === 'nushu-x';

      const shouldHydrateStoredUser =
        storedUser.role === 'doctor' ||
        (isSelfServiceUser && (!storedUser.firstName || !storedUser.lastName));

      if (shouldHydrateStoredUser) {
        void hydrateUserProfile(storedUser, {
          trackStatus: storedUser.role === 'doctor',
        }).then((hydratedUser) => {
          persistHydratedUser(storedUser, hydratedUser);
        });
      } else {
        setProfileError(null);
      }
    }
    setIsLoading(false);
  }, [hydrateUserProfile, persistHydratedUser]);

  useLayoutEffect(() => {
    function handleAuthExpired() {
      const hadSession =
        Boolean(storage.get<string>(AUTH_TOKEN_KEY)) || Boolean(storage.get<User>(AUTH_USER_KEY));
      if (!hadSession) return;

      storage.remove(AUTH_TOKEN_KEY);
      storage.remove(AUTH_USER_KEY);
      clearPatientCache();
      clearPatientProfileCache();
      clearDoctorProfileCache();
      setUser(null);
      setProfileError(null);
      setIsProfileLoading(false);
      navigate('/login', { replace: true, state: { from: location.pathname } });
      showToast('error', 'Your session expired. Please sign in again.');
    }

    window.addEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
    return () => {
      window.removeEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
    };
  }, [location.pathname, navigate, showToast]);

  const login = useCallback(
    async (credentials: LoginCredentials) => {
      setIsLoading(true);
      try {
        const response = await apiLogin(credentials);
        // Extract username from JWT access token
        // JWT payload contains: { token, isAdmin, username, exp }
        let username = credentials.userId;
        const accessToken = response.access_token;
        if (accessToken) {
          const payload = decodeJwtPayload(accessToken);
          const payloadUsername =
            payload && typeof payload.username === 'string'
              ? payload.username
              : undefined;
          if (payloadUsername) {
            username = payloadUsername;
          } else {
            warn('[Auth] Failed to decode JWT, using credentials.userId');
          }
        }

        // API doesn't return role in response, so we rely solely on username prefix
        const apiRole = response.user?.role;

        // Validate role
        const validationError = validateUserRole(username, apiRole);
        if (validationError) {
          logError('[Auth] Role validation failed:', validationError);
          throw new Error(`Authentication failed: ${validationError}`);
        }

        // Use username-based role as source of truth
        const role = getUserRole(username);
        // Extract user info from response if available
        const firstName = response.user?.first_name;
        const lastName = response.user?.last_name;
        const email = response.user?.email;

        const newUser: User = {
          id: username,
          username: username,
          role,
          firstName,
          lastName,
          email,
        };

        storage.set(AUTH_TOKEN_KEY, response.access_token);
        storage.set(AUTH_USER_KEY, newUser);
        setUser(newUser);

        const redirectPath = role === 'doctor' ? '/doctor' : '/patient';
        const from = (location.state as { from?: string })?.from;
        navigate(from || redirectPath, { replace: true });

        showToast('success', `Welcome!`);

        void hydrateUserProfile(newUser, {
          trackStatus: newUser.role === 'doctor',
        }).then((hydratedUser) => {
          persistHydratedUser(newUser, hydratedUser);
        });
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Login failed';
        showToast('error', message);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [hydrateUserProfile, navigate, location.state, persistHydratedUser, showToast]
  );

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } catch {
      // Continue with local logout even if API fails
    }

    storage.remove(AUTH_TOKEN_KEY);
    storage.remove(AUTH_USER_KEY);
    clearPatientCache();
    clearPatientProfileCache();
    clearDoctorProfileCache();
    setUser(null);
    setProfileError(null);
    setIsProfileLoading(false);
    navigate('/login', { replace: true });
    showToast('info', 'You have been logged out');
  }, [navigate, showToast]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        isProfileLoading,
        profileError,
        login,
        logout,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
